import React, { useCallback, useState } from 'react';

import { Button, Grid, Group, Paper, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import BackButton from 'components/BackButton';
import PageHeader from 'components/PageHeader';
import { useAuth } from 'context/auth-context';
import useCreateFlow from 'hooks/flows/useCreateFlow';
import useFlow from 'hooks/flows/useFlow';
import useFlowTriggerList from 'hooks/flows/useFlowTriggerList';
import useUpdateFlow from 'hooks/flows/useUpdateFlow';
import useGym from 'hooks/gym/useGym';
import { useNavigate, useParams } from 'react-router-dom';
import { Flow, FlowTriggerList } from 'types';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

// eslint-disable-next-line import/order
import DrawerEmailNotificationMessageTemplate from '../NotificationMessageTemplates/components/DrawerEmailNotificationMessageTemplate';
// eslint-disable-next-line import/order
import DrawerSMSNotificationMessageTemplate from '../NotificationMessageTemplates/components/DrawerSMSNotificationMessageTemplate';
import FlowBuilderMainContainer from './components/FlowBuilderMainContainer';
import FlowBuilderSidebar from './components/FlowBuilderSidebar';
import { CardTypes, FormValues, NotificationMessageTemplateAutoCompleteValue, TriggerTypes } from './types';

const formId = 'create-flow';

const FlowBuilder = (): JSX.Element => {
  const { hasPerm } = useAuth();
  const flowsPerm = hasPerm('FlowMutate');

  const { session } = useAuth();
  const gymData = useGym(session?.gym?.gymId);

  const { data: triggerListData, isSuccess: triggerListIsSuccess } = useFlowTriggerList();

  const isSmsEnabled = gymData?.data?.data.isSmsEnabled;

  const [triggerList, setTriggerList] = useState<FlowTriggerList>();
  const { flowId } = useParams<{ flowId: string }>();

  const { data: flowData, isSuccess: flowIsSuccess } = useFlow(flowId);
  const [type, setType] = useState<'create' | 'edit'>('create');

  const [selectedTriggerType, setSelectedTriggerType] = useState<TriggerTypes>(null);

  const createMutation = useCreateFlow();
  const updateMutation = useUpdateFlow();

  const navigate = useNavigate();
  const handleSubmitSuccess = useCallback((): void => {
    navigate(`/flows`);
  }, [navigate]);

  const form = useForm<FormValues>({
    initialValues: {
      flowId: '',
      name: '',
      trigger: null,
      recipients: null,
      notificationType: isSmsEnabled ? null : 'Email',
      enabled: true,
      notificationMessageTemplate: {
        notificationMessageTemplateId: '',
        value: '',
        label: '',
      },
      deliveryMode: 'Immediate',
      timeUnit: null,
      timeAmount: null,
      sendAtTime: null,
    },

    validate: {
      trigger: value => (value ? null : 'You must choose a trigger'),
      recipients: value => (value ? null : 'You must choose valid recipients'),
      notificationType: value => (value ? null : 'You must choose a notification type'),
      name: value => (type === 'create' && value.trim().length === 0 ? 'Flow name is required' : null),
    },
  });

  const handleTimeUnitChange = (value: Flow['timeUnit']): void => {
    form.setFieldValue('timeUnit', value);
  };

  const handleTimeAmountChange = (value: number | null): void => {
    form.setFieldValue('timeAmount', value);
  };

  const handleSendAtTimeChange = (value: string | null): void => {
    form.setFieldValue('sendAtTime', value);
  };

  const handleDeliveryModeChange = (value: Flow['deliveryMode']): void => {
    form.setFieldValue('deliveryMode', value);
    if (value === 'Immediate') {
      handleTimeUnitChange(null);
      if (form.values.trigger != 'Check-ins completed') {
        handleTimeAmountChange(null);
      }
    }
  };
  const handleSelectedTrigger = (value: Flow['trigger']): void => {
    if (triggerListIsSuccess && triggerListData.data) {
      form.setFieldValue('trigger', value);
      const isReminderTrigger =
        triggerListIsSuccess && !triggerListData.data.immediateAndDelayedTriggerList.includes(value);

      setSelectedTriggerType(isReminderTrigger ? 'reminder' : 'immediateAndDelayed');
      if (form.values.recipients) {
        if (
          form.values.recipients === 'Athletes' &&
          triggerList?.athletesRecipientDisallowedTriggerList.includes(value)
        ) {
          form.setFieldValue('recipients', null);
        } else if (
          form.values.recipients === 'Coaches' &&
          triggerList?.coachesRecipientDisallowedTriggerList.includes(value)
        ) {
          form.setFieldValue('recipients', null);
        }
      }
      if (value === 'Check-ins completed') {
        handleDeliveryModeChange('Immediate');
      } else if (
        !isReminderTrigger &&
        (form.values.deliveryMode === 'Reminder' || form.values.deliveryMode == undefined)
      ) {
        handleDeliveryModeChange('Immediate');
      } else if (isReminderTrigger) {
        handleDeliveryModeChange('Reminder');
      }
    }
  };

  useShallowEffect(() => {
    if (flowData?.data && flowIsSuccess && triggerListIsSuccess && triggerListData?.data) {
      const flow = flowData.data;
      const selectedNotificationMessageTemplate = {
        notificationMessageTemplateId: flow.notificationMessageTemplate
          ? flow.notificationMessageTemplate.notificationMessageTemplateId
          : '',
        value: '',
        label: flow.notificationMessageTemplate ? flow.notificationMessageTemplate.description : '',
      };
      form.setFieldValue('notificationMessageTemplate', selectedNotificationMessageTemplate);
      form.setFieldValue('flowId', flow.flowId);
      form.setFieldValue('name', flow.name ?? '');
      form.setFieldValue('recipients', flow.recipients);
      form.setFieldValue('notificationType', flow.notificationType);
      handleDeliveryModeChange(flow.deliveryMode);
      handleTimeUnitChange(flow.timeUnit);
      handleSelectedTrigger(flow.trigger);
      handleTimeAmountChange(flow.timeAmount);
      handleSendAtTimeChange(flow.sendAtTime);
      setType('edit');
    }
  }, [flowData?.data, flowIsSuccess, handleDeliveryModeChange, triggerListIsSuccess, triggerListData?.data]);

  const handleNotificationMessageTemplateChange = (
    item: NotificationMessageTemplateAutoCompleteValue | null,
  ): void => {
    const selectedNotificationMessageTemplate = {
      notificationMessageTemplateId: item ? item.notificationMessageTemplateId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('notificationMessageTemplate', selectedNotificationMessageTemplate);
  };

  const [selectedCard, setSelectedCard] = useState<CardTypes>(flowId ? null : 'trigger');
  const { infoNotification } = notificationHandler();
  const handleCardSelect = (cardType: CardTypes): void => {
    if (cardType == 'notificationMessageTemplate' && !form.values.notificationType) {
      infoNotification(
        'Please Select Notification Type',
        'Please Select Notification Type Before Selecting The Template',
      );
    } else setSelectedCard(cardType);
  };

  useShallowEffect(() => {
    if (triggerListData?.data && triggerListIsSuccess) {
      setTriggerList(triggerListData.data);
    }
  }, [triggerListData]);

  const [
    isCreateNotificationMessageTemplateDrawerOpen,
    { toggle: handleCreateNotificationMessageTemplateDrawerToggle },
  ] = useDisclosure(false);

  const closeNotificationMessageTemplateDrawer = (): void => {
    if (isCreateNotificationMessageTemplateDrawerOpen) {
      handleCreateNotificationMessageTemplateDrawerToggle();
    }
  };

  const handleCancel = (): void => {
    navigate(`/flows`);
  };

  const handleSubmit = (values: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();
    const valid = form.validate();
    if (valid.hasErrors) {
      return;
    }
    if (values.notificationType && values.trigger && values.recipients && values.deliveryMode) {
      if (type == 'create') {
        createMutation.mutate(
          {
            flowId: uuidv4(),
            name: values.name.trim() || null,
            trigger: values.trigger,
            notificationType: values.notificationType,
            notificationMessageTemplate: values.notificationMessageTemplate.notificationMessageTemplateId,
            recipients: values.recipients,
            enabled: values.enabled,
            deliveryMode: values.deliveryMode,
            timeUnit: values.timeUnit,
            timeAmount: values.timeAmount,
            sendAtTime: values.sendAtTime,
          },
          {
            onError: error => {
              errorNotification('Flow Creation Failed', error.response?.data.message);
            },
            onSuccess: () => {
              handleSubmitSuccess();
              successNotification('Flow Created', 'Flow has been created successfully');
            },
          },
        );
      }
      if (type == 'edit') {
        updateMutation.mutate(
          {
            flowId: values.flowId,
            name: values.name.trim() || null,
            trigger: values.trigger,
            notificationType: values.notificationType,
            notificationMessageTemplate: values.notificationMessageTemplate.notificationMessageTemplateId,
            recipients: values.recipients,
            enabled: values.enabled,
            deliveryMode: values.deliveryMode,
            timeUnit: values.timeUnit,
            timeAmount: values.timeAmount,
            sendAtTime: values.sendAtTime,
          },
          {
            onError: error => {
              errorNotification('Flow Update Failed', error.response?.data.message);
            },
            onSuccess: () => {
              handleSubmitSuccess();
              successNotification('Flow Updated', 'Flow has been updated successfully');
            },
          },
        );
      }
    }
  };

  const handleCreateNotificationMessageTemplateClick = (): void => {
    if (form.values.notificationType) {
      handleCreateNotificationMessageTemplateDrawerToggle();
    }
  };

  const handleOptionSelected = (cardType: CardTypes): void => {
    if (cardType == 'trigger') {
      setSelectedCard('delivery');
    } else if (cardType == 'delivery') {
      setSelectedCard('recipients');
    } else if (cardType == 'recipients') {
      setSelectedCard('notificationType');
    } else if (cardType == 'notificationType') {
      setSelectedCard('notificationMessageTemplate');
    }
  };

  return (
    <>
      <Paper
        shadow='xl'
        radius='md'
        withBorder
        py='sm'
        px={0}
        style={{ minWidth: 'fit-content', height: 'fit-content' }}
      >
        <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
          <PageHeader>
            <Group position='apart' align='center' noWrap>
              <Group align='center' noWrap>
                <BackButton handleOnClick={handleCancel} />
                <Title order={1} size='h1'>
                  Flow Builder
                </Title>
              </Group>
              <Group>
                <TextInput
                  aria-label='Flow name'
                  placeholder='Flow name'
                  maxLength={255}
                  {...form.getInputProps('name')}
                  data-cy='flowNameInput'
                />
                <Button color='red' onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  type='submit'
                  color='green'
                  disabled={
                    !form.values.trigger ||
                    !form.values.recipients ||
                    !form.values.notificationType ||
                    !form.values.notificationMessageTemplate.notificationMessageTemplateId ||
                    !flowsPerm
                  }
                  data-cy='createFlowButton'
                >
                  Save
                </Button>
              </Group>
            </Group>
          </PageHeader>
          <Grid>
            <Grid.Col span={8}>
              <FlowBuilderMainContainer
                onCardSelect={handleCardSelect}
                form={form}
                selectedCard={selectedCard}
                permission={flowsPerm}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <FlowBuilderSidebar
                selectedCard={selectedCard}
                form={form}
                handleNotificationMessageTemplateChange={handleNotificationMessageTemplateChange}
                handleCreateNotificationMessageTemplate={handleCreateNotificationMessageTemplateClick}
                handleOptionSelected={handleOptionSelected}
                triggerList={triggerList}
                permission={flowsPerm}
                selectedTriggerType={selectedTriggerType}
                handleSelectedTrigger={handleSelectedTrigger}
                handleDeliveryModeChange={handleDeliveryModeChange}
                handleTimeUnitChange={handleTimeUnitChange}
                handleTimeAmountChange={handleTimeAmountChange}
                handleSendAtTimeChange={handleSendAtTimeChange}
                selectedDeliveryMode={form.values.deliveryMode}
                selectedTimeAmount={form.values.timeAmount}
                selectedTimeUnit={form.values.timeUnit}
                sendAtTime={form.values.sendAtTime}
                isSmsEnabled={isSmsEnabled === undefined ? true : isSmsEnabled}
              />
            </Grid.Col>
          </Grid>
        </form>
        <DrawerEmailNotificationMessageTemplate
          opened={form.values.notificationType === 'Email' && isCreateNotificationMessageTemplateDrawerOpen}
          formId='email-notification-message-template-form'
          handleNotificationMessageTemplateChange={handleNotificationMessageTemplateChange}
          closeHandler={closeNotificationMessageTemplateDrawer}
          permission={flowsPerm}
          trigger={form.values.trigger ? form.values.trigger : undefined}
          recipients={form.values.recipients ? form.values.recipients : undefined}
        />
        <DrawerSMSNotificationMessageTemplate
          opened={form.values.notificationType === 'SMS' && isCreateNotificationMessageTemplateDrawerOpen}
          formId='sms-notification-message-template-form'
          handleNotificationMessageTemplateChange={handleNotificationMessageTemplateChange}
          closeHandler={closeNotificationMessageTemplateDrawer}
          permission={flowsPerm}
          trigger={form.values.trigger ? form.values.trigger : undefined}
          recipients={form.values.recipients ? form.values.recipients : undefined}
        />
      </Paper>
    </>
  );
};

export default FlowBuilder;
